/**
 * ReplayTemplate — Two paths, same line
 * ---------------------------------------------------------------
 * The reactive path runs first, then a quiet REPLAY mark, then the
 * Grey Rock alternative. No flash; a forensic transition. Resonant
 * phrases on either path isolate the field around them.
 */

import { AbsoluteFill, Sequence, useCurrentFrame, interpolate } from "remotion";
import type { SimulationContent } from "@/lib/contentGenerator";
import { planTimeline, PALETTE, inResonantWindow } from "../utils/timing";
import { BackgroundField } from "../components/BackgroundField";
import { MessageBubble } from "../components/MessageBubble";
import { CTAFrame } from "../components/CTAFrame";

const ReplayMark: React.FC<{ at: number }> = ({ at }) => {
  const frame = useCurrentFrame();
  const local = frame - at;
  if (local < 0 || local > 54) return null;
  const enter = interpolate(local, [0, 18], [0, 1], { extrapolateRight: "clamp" });
  const exit = interpolate(local, [30, 54], [1, 0], { extrapolateRight: "clamp" });
  const op = enter * exit;
  return (
    <AbsoluteFill style={{ pointerEvents: "none", opacity: op }}>
      <AbsoluteFill
        style={{
          background:
            "radial-gradient(ellipse at 50% 50%, transparent 30%, rgba(0,0,0,0.45) 80%)",
        }}
      />
      <div
        style={{
          position: "absolute",
          top: "44%",
          left: 0,
          right: 0,
          textAlign: "center",
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          fontSize: 56,
          letterSpacing: "0.55em",
          color: PALETTE.cyanSoft,
        }}
      >
        REPLAY
      </div>
      <div
        style={{
          position: "absolute",
          top: "53%",
          left: 0,
          right: 0,
          textAlign: "center",
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          fontSize: 18,
          letterSpacing: "0.5em",
          color: PALETTE.textDim,
          textTransform: "uppercase",
          opacity: 0.8,
        }}
      >
        Same line · different response
      </div>
    </AbsoluteFill>
  );
};

export const ReplayTemplate: React.FC<{ sim: SimulationContent }> = ({ sim }) => {
  const plan = planTimeline(sim);
  const frame = useCurrentFrame();
  const isolation = inResonantWindow(frame, plan.resonantFrames);
  const altTurns = plan.turns.filter((t) => t.role === "you_alt");
  const flashAt = altTurns[0] ? Math.max(0, altTurns[0].startFrame - 14) : -1;

  const push = interpolate(frame, [0, plan.ctaStartFrame], [1.0, 1.02], {
    extrapolateRight: "clamp",
  });

  return (
    <AbsoluteFill style={{ backgroundColor: PALETTE.bg }}>
      <BackgroundField isolate={isolation.intensity} />

      <Sequence durationInFrames={plan.ctaStartFrame}>
        <AbsoluteFill
          style={{
            padding: "200px 80px 160px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-end",
            transform: `scale(${push})`,
            transformOrigin: "50% 60%",
          }}
        >
          {plan.turns.map((t) => {
            const visible = frame >= t.startFrame;
            const newestIdx = plan.turns.reduce(
              (acc, x, idx) => (frame >= x.startFrame ? idx : acc),
              -1
            );
            const idx = plan.turns.findIndex((x) => x.index === t.index);
            const stackOffset = Math.max(0, newestIdx - idx);
            const isFocal =
              isolation.active && t.resonant && Math.abs(t.revealFrame - isolation.anchor) < 4;
            return (
              <MessageBubble
                key={t.index}
                turn={t}
                visible={visible}
                stackOffset={stackOffset}
                isolationActive={isolation.active}
                isFocal={isFocal}
              />
            );
          })}
        </AbsoluteFill>

        {flashAt >= 0 && <ReplayMark at={flashAt} />}

        <div
          style={{
            position: "absolute",
            top: 130,
            right: 60,
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
            fontSize: 18,
            letterSpacing: "0.42em",
            color:
              frame < (flashAt >= 0 ? flashAt : Infinity) ? "#e89a9a" : "#9be0bf",
            textTransform: "uppercase",
            opacity: 0.8,
          }}
        >
          {frame < (flashAt >= 0 ? flashAt : Infinity)
            ? "PATH · REACTIVE"
            : "PATH · GREY ROCK"}
        </div>
      </Sequence>

      <Sequence from={plan.ctaStartFrame} durationInFrames={plan.ctaFrames}>
        <CTAFrame preset="practice" />
      </Sequence>
    </AbsoluteFill>
  );
};
